import { useEffect } from "react";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";

/**
 * Anti-copy / anti-scraping deterrent for BICENTER AI content.
 * NOTE: This is UX-level protection. Determined users can bypass via dev tools or screenshots.
 * For legal enforcement, refer to the /legal page (© and terms of use).
 *
 * What is blocked:
 *  - Right-click context menu
 *  - Text selection (except in form inputs & elements with .selectable)
 *  - Copy / Cut / Save / Print keyboard shortcuts (Ctrl/Cmd+C, X, S, P)
 *  - Drag & drop of images
 *  - Print media (blank page)
 */
export const CopyProtection = () => {
  const { t } = useLanguage();

  useEffect(() => {
    const msg = t.protection?.blocked || "Contenu protégé — copie interdite.";

    const isTypingContext = (el) => {
      if (!el) return false;
      const tag = el.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return true;
      if (el.isContentEditable) return true;
      // Explicit opt-in via .selectable / .copyable
      if (el.closest?.("[data-selectable='true'], .selectable, .copyable")) return true;
      return false;
    };

    const blockWithToast = (e) => {
      e.preventDefault();
      e.stopPropagation();
      toast.error(msg, { duration: 2000 });
      return false;
    };

    const onContextMenu = (e) => {
      if (isTypingContext(e.target)) return;
      blockWithToast(e);
    };

    const onCopyCut = (e) => {
      if (isTypingContext(e.target)) return;
      blockWithToast(e);
    };

    const onKeyDown = (e) => {
      const key = e.key?.toLowerCase();
      const meta = e.ctrlKey || e.metaKey;
      // Ctrl/Cmd + C / X / S / P / A
      if (meta && ["c", "x", "s", "p"].includes(key)) {
        if (isTypingContext(e.target)) return;
        blockWithToast(e);
      }
      // Ctrl/Cmd + A (select all) on content
      if (meta && key === "a" && !isTypingContext(e.target)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const onDragStart = (e) => {
      if (isTypingContext(e.target)) return;
      e.preventDefault();
    };

    document.addEventListener("contextmenu", onContextMenu);
    document.addEventListener("copy", onCopyCut);
    document.addEventListener("cut", onCopyCut);
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("dragstart", onDragStart);

    return () => {
      document.removeEventListener("contextmenu", onContextMenu);
      document.removeEventListener("copy", onCopyCut);
      document.removeEventListener("cut", onCopyCut);
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("dragstart", onDragStart);
    };
  }, [t]);

  return null;
};
